import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TaskApi, TaskDTO, TaskFilterDTO, ReportTaskDTO } from './task/TaskApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faChevronDown, faChevronRight ,faFileUpload} from '@fortawesome/free-solid-svg-icons';
import { TaskStatusOptions } from '../../constants/taskConstants';
import { EntityType, UploadContexts } from '../../constants/globalConstants';
import  ProjectTaskActions from './task/ProjectTaskActions';
import BulkTaskUploadModal from './task/BulkTaskUploadModal';

// Extend task for tree structure
type NestedTaskDTO = ReportTaskDTO & { children?: NestedTaskDTO[] };

export const SelectedProjectTaskList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, projectName, projectType, description, status } = location.state || {};
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const tenantName = user?.tenantName || '';
  const userId = user?.id || '';
  const username = user?.username || '';

  const [tasks, setTasks] = useState<NestedTaskDTO[]>([]);
  const [expandedTaskIds, setExpandedTaskIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);

  const handleSuccess = () => {
    console.log("Bulk upload success");
    alert('Tasks uploaded successfully!');
    handleSearch();
    setShowBulkModal(false); // close modal
  };

  const handleError = () => {
    console.log("Bulk upload failed");
    alert('Tasks uploaded failed!');
    handleSearch();
   // setShowBulkModal(false); // close modal
  };


  const handleSearch = async () => {
    setLoading(true);
    try {
      const filters: TaskFilterDTO = { projectId, tenantId };
      const res = await TaskApi.filter(filters);
      setTasks(res.data);
    } catch (err) {
      setError('Failed to fetch task data.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this task?')) {
      await TaskApi.delete(id);
      handleSearch();
    }
  };

  const handleCreateTask = (task: TaskDTO, levelNumber: string) => {
    navigate('/project-tasks/create', {
      state: {
        tenantId,
        tenantName,
        projectName: task.projectName,
        parentId: task.id,
        title: task.title,
        projectId: task.projectId,
        contractId: task.contractId,
        description: task.description,
        levelNumber,
      },
    });
  };

  const toggleExpand = (taskId: string) => {
    setExpandedTaskIds(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getRowBackgroundColor = (depth: number): string => {
    const colorMap: Record<number, string> = {
      0: 'bg-white',
      1: 'bg-blue-50',
      2: 'bg-green-50',
      3: 'bg-yellow-50',
      4: 'bg-purple-50',
    };
    return colorMap[depth] || 'bg-gray-100';
  };

  const renderNestedTasks = (
    taskList: NestedTaskDTO[],
    prefix = '',
    depth = 0
  ): JSX.Element[] => {
    return taskList.flatMap((task, index) => {
      const levelNumber = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
      const isExpanded = expandedTaskIds[task.id];
      const hasChildren = task.children && task.children.length > 0;
      const rowBgColor = getRowBackgroundColor(depth);
      const indent = levelNumber.split('.').length * 16;
      const maxLevelReached = levelNumber.split('.').length >= 4;

      const row = (
        <tr key={task.id} className={`hover:bg-gray-100 ${rowBgColor}`}>
          <td className="p-2 border text-center">
            {hasChildren && (
              <button onClick={() => toggleExpand(task.id)}>
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
              </button>
            )}
          </td>
          <td className="p-2 border text-center">{levelNumber}</td>
          <td className="p-2 border">
          <ProjectTaskActions
            task={task}
            levelNumber={levelNumber}
            handleCreateTask={handleCreateTask}
            handleDelete={handleDelete}
            maxLevelReached={maxLevelReached} 
        />
          </td>
          <td className="p-2 border" style={{ paddingLeft: `${indent}px` }}>{task.title}</td>
          <td className="p-2 border">{task.projectName}</td>
          <td className="p-2 border">{task.status}</td>
          <td className="p-2 border">{task.priority}</td>
          <td className="p-2 border">{task.progress}</td>
          <td className="p-2 border">{task.description}</td>
          <td className="p-2 border">{task.taskType}</td>
          <td className="p-2 border">{task.units}</td>
          <td className="p-2 border">{task.estimatedQuantity}</td>
          <td className="p-2 border">{task.estimatedRate}</td>
          <td className="p-2 border">{task.estimatedTotalAmountWithoutTax}</td>
          <td className="p-2 border">{task.taxPercentage}</td>
          <td className="p-2 border">{task.estimatedTotalTaxAmount}</td>
          <td className="p-2 border">{task.estimatedTotalAmountWithTax}</td>
      
         
          
          <td className="p-2 border">{task.actualQuantity}</td>
          <td className="p-2 border">{task.actualRate}</td>
          <td className="p-2 border">{task.actualTotalAmountWithoutTax}</td>
          <td className="p-2 border">{task.actualTotalTaxAmount}</td>
          <td className="p-2 border">{task.actualTotalAmountWithTax}</td>
          
          <td className="p-2 border">{task.givenStartDate}</td>
          <td className="p-2 border">{task.givenEndDate}</td>
          <td className="p-2 border">{task.actualStartDate}</td>
          <td className="p-2 border">{task.actualEndDate}</td>
          <td className="p-2 border">{task.comment}</td>
          <td className="p-2 border">{task.rejectionReason}</td>
           
                
        </tr>
      );

      const childrenRows = hasChildren && isExpanded ? renderNestedTasks(task.children!, levelNumber, depth + 1) : [];
      return [row, ...childrenRows];
    });
  };

  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <div className="p-6 bg-white rounded shadow">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center text-sm text-blue-600 hover:underline"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
      </button>

      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-xl font-bold">Project Task List</h1>
        <button
          onClick={() => navigate('/project-tasks/create', {
            state: { projectId, projectName, projectType, description, status, tenantId, tenantName }
          })}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center text-sm"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" /> Create Task
        </button>
        <button
          onClick={() => setShowBulkModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center text-sm "
        >
          <FontAwesomeIcon icon={faFileUpload} className="mr-2" /> Bulk Upload Task
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 bg-gray-50 p-4 rounded border">
        <div>
          <label className="block text-sm font-medium text-gray-600">Project Name</label>
          <div className="bg-white border px-3 py-2 rounded">{projectName || '-'}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">Project Type</label>
          <div className="bg-white border px-3 py-2 rounded">{projectType || '-'}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600">Status</label>
          <div className="bg-white border px-3 py-2 rounded">{status || '-'}</div>
        </div>
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-600">Description</label>
          <div className="bg-white border px-3 py-2 rounded">{description || '-'}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border w-8 text-center">▶</th>
                <th className="p-2 border text-center">Level</th>
                <th className="p-2 border">Actions</th>
                <th className="p-2 border">Title</th>
                <th className="p-2 border">Project</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Priority</th>
                <th className="p-2 border">Progress (%)</th>
                <th className="p-2 border">Description</th>
                <th className="p-2 border">Task Type</th>
                <th className="p-2 border">Units</th>
                <th className="p-2 border">Estimated Quantity</th>
                <th className="p-2 border">Estimated Rate</th>
                <th className="p-2 border">Estimated Total Amount Without Tax </th>
                <th className="p-2 border">Tax (%)</th>
                <th className="p-2 border">Estimated Total Tax Amount</th>
                <th className="p-2 border">Estimated Total Amount With Tax</th>
               
                <th className="p-2 border">Actual Quantity</th>
                <th className="p-2 border">Actual Rate</th>
                <th className="p-2 border">Actual Total Amount WithoutT ax </th>
                <th className="p-2 border">Actual Total Tax Amount</th>
                <th className="p-2 border">Actual Total Amount With Tax </th>
               
               
                <th className="p-2 border">Given Start Date</th>
                <th className="p-2 border">Given End Date</th>
                <th className="p-2 border">Actual Start Date</th>
                <th className="p-2 border">Actual End Date</th>
                <th className="p-2 border">Comments</th>
                <th className="p-2 border">Rejection Reason</th>
              </tr>
            </thead>
            <tbody>{renderNestedTasks(tasks)}</tbody>
          </table>
        </div>
      )}


    {showBulkModal && (
      <BulkTaskUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={handleSuccess}
        onError={handleError}
        projectId={projectId}
        projectName={projectName}
        tenantId={tenantId}
        tenantName={tenantName}
        userId={userId}
        username={username}
        status={status}
        description={description}
      />
    )}
    </div>
  );
};